/**
 * Shared input mapping + validation for the Trade360 External Live Signals
 * group config — `liveSignalSources` (a fixed preset, on/off — there is
 * exactly one real combination in use, so this is a switch, not a free-text
 * editor) and `liveSignalTuning` (`pressureArmThreshold` /
 * `pressureWindowMs` / `pressureReleaseOnRepricedTicks`).
 *
 * Mirrors `counterHedgeLadder.ts`'s shape: pure parse/format helpers kept out
 * of the component so they are unit-testable without mounting MUI, and an
 * equality helper so the group dialog can send `liveSignalSources` /
 * `liveSignalTuning` on the wire ONLY when the operator actually changed
 * them — an unrelated edit (a rename) must not re-send an untouched value.
 */

export const LIVE_SIGNAL_SOURCES_PRESET = [
  'pressure:lsports-trade360',
  'score_move:lsports-trade360',
  'score_revert:lsports-trade360',
  'penalty_awarded:lsports-trade360',
  'penalty_missed:lsports-trade360'
] as const;

export const LIVE_SIGNAL_SECTION_LABEL = 'Trade360 live signals';
export const LIVE_SIGNAL_SWITCH_LABEL = 'Withhold makers on the threatened side';

export const LIVE_SIGNAL_ARM_THRESHOLD_LABEL = 'Pressure arm threshold';
export const LIVE_SIGNAL_ARM_THRESHOLD_HELP =
  'Consecutive pressure signals before the step-aside arms. Suggested: 1 — arma con el primer evento. Blank = engine default.';

export const LIVE_SIGNAL_WINDOW_MS_LABEL = 'Pressure window (ms)';
export const LIVE_SIGNAL_WINDOW_MS_HELP =
  'Rolling window the pressure count is measured over, in ms. Blank = engine default.';

export const LIVE_SIGNAL_RELEASE_TICKS_LABEL = 'Release on repriced ticks';
export const LIVE_SIGNAL_RELEASE_TICKS_HELP =
  'Repriced ticks on the threatened side before the step-aside releases. Blank = engine default.';

export const LIVE_SIGNAL_TUNING_FIELD_ERROR = 'Must be a non-negative integer';

export interface LiveSignalTuningInput {
  pressureArmThreshold: string;
  pressureWindowMs: string;
  pressureReleaseOnRepricedTicks: string;
}

export interface LiveSignalTuning {
  pressureArmThreshold?: number;
  pressureWindowMs?: number;
  pressureReleaseOnRepricedTicks?: number;
}

export const EMPTY_LIVE_SIGNAL_TUNING_INPUT: LiveSignalTuningInput = {
  pressureArmThreshold: '',
  pressureWindowMs: '',
  pressureReleaseOnRepricedTicks: ''
};

/** Group value -> the three input strings. `null`/absent renders all blank. */
export function liveSignalTuningToInput(
  tuning: LiveSignalTuning | null | undefined
): LiveSignalTuningInput {
  return {
    pressureArmThreshold:
      tuning?.pressureArmThreshold != null
        ? String(tuning.pressureArmThreshold)
        : '',
    pressureWindowMs:
      tuning?.pressureWindowMs != null ? String(tuning.pressureWindowMs) : '',
    pressureReleaseOnRepricedTicks:
      tuning?.pressureReleaseOnRepricedTicks != null
        ? String(tuning.pressureReleaseOnRepricedTicks)
        : ''
  };
}

export interface ParsedLiveSignalTuning {
  /** `null` when every field is blank — "no tuning object", engine defaults. */
  value: LiveSignalTuning | null;
  /** Per-field message for whichever inputs failed to parse. Non-empty means
   *  the caller must NOT save — `value` excludes the offending field(s), and
   *  shipping it would silently ship a wrong / incomplete tuning object. */
  errors: Partial<Record<keyof LiveSignalTuningInput, string>>;
}

function parseNonNegativeInt(raw: string): {
  value: number | undefined;
  error: string | null;
} {
  const trimmed = raw.trim();
  if (trimmed === '') return { value: undefined, error: null };
  const n = Number(trimmed);
  if (!Number.isInteger(n) || n < 0) {
    return { value: undefined, error: LIVE_SIGNAL_TUNING_FIELD_ERROR };
  }
  return { value: n, error: null };
}

/**
 * The three inputs -> the `liveSignalTuning` object, or `null` when every
 * field is blank.
 */
export function parseLiveSignalTuningInput(
  input: LiveSignalTuningInput
): ParsedLiveSignalTuning {
  const arm = parseNonNegativeInt(input.pressureArmThreshold);
  const windowMs = parseNonNegativeInt(input.pressureWindowMs);
  const release = parseNonNegativeInt(input.pressureReleaseOnRepricedTicks);

  const errors: ParsedLiveSignalTuning['errors'] = {};
  if (arm.error) errors.pressureArmThreshold = arm.error;
  if (windowMs.error) errors.pressureWindowMs = windowMs.error;
  if (release.error) errors.pressureReleaseOnRepricedTicks = release.error;

  const value: LiveSignalTuning = {};
  if (arm.value !== undefined) value.pressureArmThreshold = arm.value;
  if (windowMs.value !== undefined) value.pressureWindowMs = windowMs.value;
  if (release.value !== undefined)
    value.pressureReleaseOnRepricedTicks = release.value;

  return {
    value: Object.keys(value).length > 0 ? value : null,
    errors
  };
}

/** Deep-equal for the small tuning object, `null`/absent treated the same. */
export function liveSignalTuningEqual(
  a: LiveSignalTuning | null,
  b: LiveSignalTuning | null | undefined
): boolean {
  const right = b ?? null;
  if (a === right) return true;
  if (a == null || right == null) return false;
  return (
    (a.pressureArmThreshold ?? null) === (right.pressureArmThreshold ?? null) &&
    (a.pressureWindowMs ?? null) === (right.pressureWindowMs ?? null) &&
    (a.pressureReleaseOnRepricedTicks ?? null) ===
      (right.pressureReleaseOnRepricedTicks ?? null)
  );
}

/**
 * `liveSignalSources` -> whether the group's toggle should render ON. Any
 * non-empty array counts, not just an exact preset match, so a group whose
 * saved sources already differ slightly from the current preset still reads
 * as "on" instead of silently offering to wipe it via `[]`.
 */
export function liveSignalSourcesEnabled(
  sources: readonly string[] | null | undefined
): boolean {
  return !!sources && sources.length > 0;
}

export function liveSignalSourcesEqual(
  a: readonly string[],
  b: readonly string[] | null | undefined
): boolean {
  const right = b ?? [];
  if (a.length !== right.length) return false;
  return a.every((v, i) => v === right[i]);
}
